/**
 * Task workflow rules — the business logic that used to be interleaved with JSX
 * in `StudyCard.tsx` (589 lines) and `TechnicianQueuePage.tsx` (490 lines).
 *
 * Pure functions only: no JSX, no React, no I/O. Several of these mirror a rule
 * the api-gateway enforces server-side; where they do, the comment names the
 * server symbol, because the client copy exists only so the user doesn't
 * discover the rule as an error toast after acting. If a server rule changes,
 * both sides change.
 */
import type { ChecklistState, Role, StudyRow, Task, TaskKind, TaskStatus } from '@/api/types'
import { ohifUrl } from '@/lib/ohif'

/**
 * OHIF mode a work card can open.
 *
 * - `basic`    → `@ohif/mode-longitudinal` (read-only viewer; QA + post-submit review)
 * - `tagging`  → `@medimaging/mode-tagging` (label-map + contour editor for mask work)
 * - `anomaly`  → `@medimaging/mode-anomaly` (the radiologist's anomaly read — the
 *                same label-map editor, because a read is scored as a mask; its
 *                contour tools are folded into that mask, never saved as outlines)
 *
 * Mode is selected by the URL path; the `defaultMode` in app-config only applies
 * when the path is absent. `ViewerMode` and `OhifRouteKey` are the same three
 * keys — the path segment each maps to lives in lib/ohif.ts, because it is the
 * mode's `routeName` and not its package name (that mismatch is what made the
 * read-only button open a blank page).
 */
export type ViewerMode = 'basic' | 'basicPlain' | 'tagging' | 'anomaly' | 'ccta' | 'cctaReadOnly'

/** Phase of the local claim → annotate → submit handshake for one task. */
export type AnnotatePhase = 'idle' | 'annotating' | 'pending_submit'

// Client mirror of api-gateway `task_kinds.py:STUDY_OWNED_KINDS`. These rows are
// owned by a study-level workflow and have no `task-{id}` workflow, so a review
// signal for one has nowhere to land (the gateway 409s). Only the paired
// `organ_mask_review` row carries a decision, and it signals
// `organ-mask-{study_uid}`.
export const STUDY_OWNED: ReadonlySet<TaskKind> = new Set<TaskKind>([
  'organ_mask_edit',
  'organ_mask_review',
  'vessel_mask_edit',
  'anomaly_annotation',
  // A review row whose subject is a clinical read rather than an organ mask. Only
  // the workflow engine mints these, so it is study-owned like the rest — but it
  // DOES carry a decision, which is why `canDecide` needs its own branch below.
  'anomaly_review',
  'pathology_annotation',
])

/**
 * My own `approved` verdict on this mask, from any round.
 *
 * Client mirror of the gateway's `review_remarks.has_approved_mask`. The exact
 * `'approved'` match is what excludes a `maintainer:override_approve` — an
 * override is the maintainer's act, not the radiologist's, and it must not read
 * as one of them having verified the mask.
 *
 * Any iteration counts: if I approved v1 and my colleague sent it back, I do not
 * re-verify v2. My read is already open and stays open.
 */
export function myMaskApproval(task: Task, userId: string): boolean {
  if (!userId) return false
  return (task.review_remarks ?? []).some(
    (r) => r.reviewer_id === userId && String(r.decision ?? '') === 'approved',
  )
}

/**
 * Is phase two — the anomaly read — open for THIS radiologist?
 *
 * Client mirror of the gateway's `review_remarks.reader_phase_two_open`, and the
 * whole of the per-radiologist unlock: the reads used to open per *study*, when
 * both radiologists had approved. They open per *reader* now, on that reader's own
 * approval, so one radiologist can file a rework round while the other is already
 * reading. `anomaly_state` alone therefore stopped being the phase switch.
 *
 * Two ways in, both needed. My own approval is the ordinary one. A `finalized`
 * mask is the other: it covers a radiologist a maintainer's `override_approve`
 * overruled (they never approved, and a study still needs two reads to compare),
 * and every in-flight study started before this change, where the reads opened
 * only once the mask had finalized for everyone.
 */
export function readerPhaseTwoOpen(task: Task, userId: string): boolean {
  return task.seg_state === 'finalized' || myMaskApproval(task, userId)
}

/**
 * The radiologist's ticket on an anomalous study is ONE row covering two phases:
 * verify the technician's mask, then read for anomalies on it. `anomaly_state`
 * is the workflow's phase switch for the *study* — `AnomalyWorkflow` patches it to
 * `annotating` once the first approval lands, having adopted this very
 * `organ_mask_review` row as its task instead of creating a second one — and
 * `readerPhaseTwoOpen` is the per-reader half, because at that moment the second
 * radiologist may not have voted at all.
 *
 * Every consumer of "is this an anomaly read" goes through here or through the
 * `anomaly_annotation` kind, which is the same ticket on a study that had no mask
 * to review (nothing to merge with) — see `isAnomalyRead`.
 */
export function anomalyPhase(task: Task, userId: string): boolean {
  return anomalyPhaseOpen(task) && readerPhaseTwoOpen(task, userId)
}

/**
 * The study's anomaly phase is open on this row — for *somebody*. Not the same
 * question as `anomalyPhase`, and only the right one where the answer isn't about
 * a particular user's next action (a reviewer's "what's waiting on a second
 * reader" list). Anything that offers a viewer, a submit or a verdict wants the
 * per-reader helper instead.
 */
export function anomalyPhaseOpen(task: Task): boolean {
  return task.task_kind === 'organ_mask_review' && task.anomaly_state === 'annotating'
}

/** Anomaly-read work, whether it is a merged ticket's second phase or a row of
 *  its own. `pathology_annotation` is the legacy shape and keeps its own checks. */
export function isAnomalyRead(task: Task, userId: string): boolean {
  return task.task_kind === 'anomaly_annotation' || anomalyPhase(task, userId)
}

/** My own read is already filed on a row that still waits for the other reader. */
export function mySubmissionIn(task: Task, userId: string): boolean {
  return (task.submitted_by ?? []).includes(userId)
}

/**
 * `/ohif/<mode>?StudyInstanceUIDs=…` — every viewer URL in the shell.
 *
 * `taskId` is not a breadcrumb here: the pre-annotation QC checklist panel in
 * the viewer reads it off its own URL (`preflight.js:taskIdFromSearch`) to know
 * which task it is gating, and the checklist is per (task, user) — one study can
 * carry a technician's mask edit and two radiologists' reads at once, each
 * filing a different variant. Without it the panel has no claim to file against
 * and says so instead of rendering the form.
 */
export function viewerUrl(studyUid: string, mode: ViewerMode, taskId?: string): string {
  return ohifUrl(mode, studyUid, taskId ? { taskId } : undefined)
}

/**
 * URL for the read-only Basic viewer. Used by read-only callers (review/QA,
 * post-submit re-view, the Activity list).
 *
 * `taskKind` and `taskId` ride along as breadcrumbs for in-tab debugging /
 * audit; mode selection is path-based, not param-based, so the legacy
 * `readOnly=1` flag is gone. The route segment comes from `OHIF_ROUTE` — see
 * `lib/ohif.ts` for why it is not the mode's package name.
 */
export function buildOhifUrl(task: Task, _opts?: { readOnly?: boolean }): string {
  return ohifUrl('basic', task.study_uid, {
    taskKind: task.task_kind,
    taskId: task.id,
  })
}

/**
 * Why this submission would be refused, or `null` when it wouldn't.
 *
 * Client mirror of the gateway's `qc_checklist.assert_submittable`, which 409s a
 * submit with no filed `proceed` checklist — **either phase**, the pre one that
 * opened the tools and the post one that confirms the finished work — and 422s a
 * read that answered "yes, there is an abnormality" with no write-up. All of them
 * are fixed in the OHIF tab, so learning about any from a toast means reopening a
 * tab the user has just closed, which is the whole reason this copy exists.
 *
 * The post phase is read off `state.post`, the summary the *pre* response carries:
 * this function is called with the state `WorkPage` already fetched, and a second
 * round trip per card to learn one boolean isn't worth it.
 *
 * `undefined` (the state hasn't loaded, or the fetch failed) is deliberately NOT
 * a blocker. The server is the enforcement; blocking a submit because a
 * supplementary GET failed would invent a rule out of a network error.
 */
export function submitBlocker(state?: ChecklistState): string | null {
  if (!state || !state.gated) return null
  if (state.outcome === 'artifact_reject') {
    return 'You rejected this study as severely artefacted. A radiologist decides that next — there is nothing to submit here.'
  }
  if (state.outcome === 'no_abnormality') {
    return 'You filed this read as "no abnormality", which closes the study. There is nothing to submit.'
  }
  if (state.outcome !== 'proceed') {
    return "Fill the pre-annotation QC checklist in the viewer's left panel before submitting."
  }
  // The description counts from EITHER row, which is what `assert_submittable`
  // accepts: the pre radiologist checklist used to own the write-up, so a study
  // filed before the post checklists shipped has it on the pre row.
  const described =
    state.post?.description_present === true || !!(state.findings_description ?? '').trim()
  if (state.post?.gated) {
    if (!state.post.filed) {
      const title = state.post.title || 'post-work checklist'
      // Names the button, because the form is no longer on screen waiting to be
      // noticed: the left panel hides it until "Done editing — open the checklist"
      // is pressed (`postSectionRevealed`), so "finish the checklist in the left
      // panel" would send the user looking for something that isn't rendered.
      return `Finish the “${title}” in the viewer's left panel — press “Done editing” there to open it. It confirms the work you just did, and the submit is refused until it is filed.`
    }
    if (state.post.description_required && !described) {
      return 'You reported an abnormality, so a description of the lesion is required. Write it in the checklist panel in the viewer tab, under the questions.'
    }
  }
  if (state.description_required && !described) {
    return 'You reported an abnormality, so a description of your findings is required. Write it in the checklist panel in the viewer tab — it saves as you go.'
  }
  return null
}

/** One date format across every card and comment. */
export function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/** `dd/mm/yyyy`, or `null` for a missing/garbled timestamp. The card's absolute
 *  date; `relativeDate` rides along as its tooltip. */
export function formatDay(iso: string): string | null {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB')
}

/** `dd/mm/yyyy, HH:MM` in the viewer's own timezone, or `null` for a missing/garbled
 *  timestamp. The card's claim time: a date alone cannot tell a ticket taken this
 *  morning from one taken at the end of yesterday. */
export function formatDateTime(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `${d.toLocaleDateString('en-GB')}, ${time}`
}

/** When the ticket was claimed, from the point of view of `userId`: their own slot if
 *  they hold one, otherwise the first claim on the row. `null` while nobody holds it,
 *  and on a row claimed before the claim columns existed. */
export function claimedAtFor(
  task: Pick<Task, 'assignee_id' | 'second_assignee_id' | 'claimed_at' | 'second_claimed_at'>,
  userId: string,
): string | null {
  if (userId && task.second_assignee_id === userId) {
    return task.second_claimed_at ?? null
  }
  if (userId && task.assignee_id === userId) return task.claimed_at ?? null
  if (task.assignee_id) return task.claimed_at ?? null
  if (task.second_assignee_id) return task.second_claimed_at ?? null
  return null
}

/**
 * Which OHIF viewer modes apply to a given task. The label-map editor is an
 * authoring tool — not appropriate for `qa_check` (the reviewer is examining
 * an existing annotation, not creating one) and not offered on an anomaly
 * read, where the radiologist outlines lesions but must never touch the mask.
 */
export function applicableViewerModes(task: Task, userId: string): ReadonlySet<ViewerMode> {
  if (task.task_kind === 'qa_check') return new Set<ViewerMode>(['basic'])
  // CCTA vessel tickets: own mode; the review has no anomaly phase, so this
  // sits ahead of the anomaly-read and generic organ_mask_review branches.
  if (task.task_kind === 'vessel_mask_edit') return new Set<ViewerMode>(['ccta'])
  if (task.task_kind === 'organ_mask_review' && task.viewer === 'ccta')
    return new Set<ViewerMode>(['cctaReadOnly'])
  // Anomaly read: the ROI tools, plus the CT with no mask overlay. On a merged
  // ticket the reader has already seen the mask — they just verified it in phase
  // one — so `basicPlain`'s "don't bias the reader" rationale is spent there; it
  // stays because a clean CT is still the better surface to outline on.
  //
  // Per reader, not per study: while one radiologist reads, the other may still be
  // verifying the mask, and handing them the anomaly editor would ask them to
  // outline lesions on paint they have not approved.
  if (isAnomalyRead(task, userId)) return new Set<ViewerMode>(['basicPlain', 'anomaly'])
  // Technician's organ-mask edit: only surface "Edit mask". The "Open
  // viewer" (basic mode) button opened the SAME URL as Edit mask so it
  // was redundant here — the tech never wanted a read-only view while
  // in the middle of editing.
  if (task.task_kind === 'organ_mask_edit') return new Set<ViewerMode>(['tagging'])
  // Verifying the technician's mask: read-only. A reviewer never paints — a
  // radiologist holds no `segmentation:write`, and a disapproval sends the mask
  // back to the technician rather than fixing it in place. Offering `tagging`
  // here also dragged the *technician's* lock onto the reviewer's card: "Out for
  // review — a radiologist is verifying this mask" and a "Mask locked" chip,
  // told to the radiologist whose verification that is.
  if (task.task_kind === 'organ_mask_review') return new Set<ViewerMode>(['basic'])
  // Verifying somebody else's read: read-only, and *with* the overlay — the read
  // IS a mask, so the plain CT would hide the thing being judged. No `tagging`,
  // which is the authoring surface for a mask this radiologist must not touch.
  if (task.task_kind === 'anomaly_review') return new Set<ViewerMode>(['basic'])
  return new Set<ViewerMode>(['basic', 'tagging'])
}

/**
 * Why the mask can't be edited right now, or `null` when it can.
 *
 * The server-side 409 in `_assert_mask_writable` is the enforcement; this
 * exists so the technician doesn't discover the lock as an error toast after
 * opening the editor and drawing. `seg_state === null` is a pre-redesign study,
 * which the server treats as unlocked — so do we.
 */
export function maskLockReason(task: Task, reworkLimit: number): string | null {
  const s = task.seg_state
  if (!s || s === 'editing') return null
  switch (s) {
    case 'auto_seg_running':
      return 'Auto-segmentation is still running'
    case 'auto_seg_failed':
      return 'Auto-segmentation failed — waiting on a maintainer decision'
    case 'review':
      return 'Out for review — a radiologist is verifying this mask'
    case 'awaiting_maintainer':
      return `Escalated to a maintainer after ${reworkLimit} ${
        reworkLimit === 1 ? 'disapproval' : 'disapprovals'
      }`
    case 'finalized':
      return 'Finalized — this mask is permanently immutable'
    case 'quarantine':
      return 'Study quarantined'
    case 'not_applicable':
      return 'This study has no mask work'
    default:
      return 'Mask is locked'
  }
}

export interface ReviewProgress {
  /** Distinct reviewer verdicts recorded for the current round. */
  votes: number
  /** How many the workflow needs before the mask moves. */
  required: number
  /** Still outstanding. 0 once the gate can clear. */
  outstanding: number
  /** This user's decision this round, or null if they haven't ruled. */
  myDecision: string | null
  /** I have ruled and the round is still short of `required`. */
  waiting: boolean
}

/**
 * Where a mask review round stands.
 *
 * `OrganMaskWorkflow` waits for `max_annotators` DISTINCT reviewer verdicts
 * (`segmentation.review_count`, default 2) and dedups `_review_votes` by
 * reviewer. So a lone radiologist clicking "Send for rework" a second time
 * re-sends the same vote and nothing moves — and until the gateway started
 * recording verdicts on the row at request time, the first verdict left no trace
 * at all, so the card looked untouched and the reviewer had no way to tell the
 * click had worked.
 *
 * Counts only this round (`iteration === rejection_count + 1`) and only reviewer
 * verdicts — a `maintainer:` entry is an override, not a vote toward the gate.
 */
export function reviewProgress(task: Task, userId: string): ReviewProgress {
  const required = Math.max(1, task.max_annotators || 1)
  const iteration = (task.rejection_count || 0) + 1
  const mine = (task.review_remarks ?? []).filter(
    (r) =>
      Number(r.iteration ?? 0) === iteration &&
      !String(r.decision ?? '').startsWith('maintainer:'),
  )
  const byReviewer = new Map(mine.map((r) => [r.reviewer_id, r]))
  const votes = byReviewer.size
  const myDecision = (byReviewer.get(userId)?.decision as string | undefined) ?? null
  return {
    votes,
    required,
    outstanding: Math.max(0, required - votes),
    myDecision,
    waiting: myDecision !== null && votes < required,
  }
}

export function isAssignedToUser(task: Task, userId: string): boolean {
  return task.assignee_id === userId || task.second_assignee_id === userId
}

/**
 * "Everything I've touched" — same predicate as ``isAssignedToUser`` plus any
 * task where I acted as reviewer/maintainer via ``review_remarks``. Assigneeship
 * alone missed maintainer tiebreaks on anomaly disagreements and radiologist /
 * maintainer verdicts on organ-mask reviews: those actors sign the task from the
 * outside without ever landing in ``assignee_id`` / ``second_assignee_id``, so
 * their own Activity page hid work they had literally just done.
 */
export function isTouchedByUser(task: Task, userId: string): boolean {
  if (isAssignedToUser(task, userId)) return true
  const remarks = task.review_remarks ?? []
  return remarks.some((r) => r.reviewer_id === userId)
}

export function isClaimable(task: Task, userId: string): boolean {
  if (task.status === 'pending') return true
  return (
    task.status === 'partially_claimed' &&
    task.second_assignee_id === null &&
    task.assignee_id !== userId
  )
}

export function isFullyClaimed(task: Task): boolean {
  const count =
    task.assignee_id !== null && task.second_assignee_id !== null
      ? 2
      : task.assignee_id !== null
        ? 1
        : 0
  return count >= task.max_annotators
}

export const TERMINAL_STATUSES: readonly TaskStatus[] = ['submitted', 'complete', 'under_review']

export function isTerminal(task: Task): boolean {
  return TERMINAL_STATUSES.includes(task.status)
}

// ---------------------------------------------------------------------------
// Role / kind visibility — ported verbatim from TechnicianQueuePage.tsx:252-320.
// A regression here silently shows people work they cannot do, and the gateway
// answers with a 409/403.
// ---------------------------------------------------------------------------

export interface KindVisibility {
  role: Role
  /** `me.permissions.includes('task:review')`. */
  canReview: boolean
}

/**
 * An `artifact_review` row: a technician scored Artifacts = 3 – Severe and
 * rejected the study, and a radiologist has to confirm that call or send it back.
 *
 * Never claimed and never submitted. `POST /checklists/artifact/{id}/decision`
 * asks only for `task:review` — no holder check — because the row exists to be
 * adjudicated by whichever radiologist gets to it, and a claim would be a lock on
 * a decision that takes one click. So it is filtered out of Available and In
 * progress and rendered by `ArtifactReviewCard` on the decide tab instead.
 */
export function isArtifactAdjudication(task: Task): boolean {
  return task.task_kind === 'artifact_review'
}

/** Is this row work the current user could ever act on? */
export function isVisibleKind(task: Task, { role, canReview }: KindVisibility): boolean {
  // `qc_review` is a maintainer flag from ingestion, not annotation work: it
  // has no per-task workflow, so claiming or submitting one 500s. It's
  // resolved from the Needs-decision tab via POST /ingest/review/{wfid}.
  if (task.task_kind === 'qc_review') return false
  // An artefact adjudication is a reviewer verdict on someone else's rejection.
  // Without this branch it fell through to the `return true` below and appeared
  // in the technician's Available tab with a Claim button — on a task raised
  // *because* of their own reject, and one the gateway would never let them
  // decide.
  if (isArtifactAdjudication(task)) return canReview
  if (role === 'qa') return task.task_kind === 'qa_check'
  if (task.task_kind === 'qa_check') return false
  // Anomaly annotation is a clinical read, so it's radiologist-only — the
  // gateway's claim_task raises 403 for anyone else ("the agreement rule
  // compares two independent opinions, which is meaningless if a technician can
  // fill a slot"). Admin is exempt there, so it is here.
  // Verifying a read is the same judgement on the same pixels, so it carries the
  // same restriction — and the gateway's claim gate names both kinds too.
  if (task.task_kind === 'anomaly_annotation' || task.task_kind === 'anomaly_review') {
    return role === 'radiologist' || role === 'admin'
  }
  // Verifying the technician's mask is a clinical judgement on the same pixels as
  // the read above it, so it belongs to the radiologist's claim pool and nobody
  // else's. Client mirror of `assign_task`, which names the same two roles.
  //
  // `canReview` was the old gate and it was wrong in one direction: `maintainer`
  // holds `task:review` too, so every mask-verification row reached them — once in
  // Available with a Claim button, and again in Needs decision as an
  // approve/reject card, because `canDecide`'s `ownershipExempt` lets a maintainer
  // file a verdict on a row they never claimed. Both offered a maintainer a call
  // only a radiologist is qualified to make.
  //
  // The mask's other route to a maintainer is closed at the source rather than
  // here: a review node's `on_limit` is `retry` now (see `REVIEW_ON_LIMIT` in the
  // gateway's `workflow_graph.py`), so a mask nobody will approve goes back to the
  // technician forever instead of escalating. What still legitimately reaches a
  // maintainer is a *reader disagreement*, which rides on this same adopted row —
  // routed by `groupFlaggedWork` off `requires_maintainer`, deliberately not
  // through this predicate.
  if (task.task_kind === 'organ_mask_review') {
    return role === 'radiologist' || role === 'admin'
  }
  // Editing the mask writes the working SEG, which needs `segmentation:write` —
  // a radiologist deliberately has none, so claiming one bought a row whose every
  // save 403s. Client mirror of the gateway's claim gate in
  // `routers/tasks.py::assign_task`.
  if (task.task_kind === 'organ_mask_edit' || task.task_kind === 'organ_mask_segmentation') {
    return role === 'technician' || role === 'maintainer' || role === 'admin'
  }
  // A CCTA vessel edit is the radiologist's. A technician cannot claim it (the
  // gateway 403s); maintainers see it but cannot claim either.
  if (task.task_kind === 'vessel_mask_edit') {
    return role === 'radiologist' || role === 'maintainer' || role === 'admin'
  }
  return true
}

/**
 * A row that carries a review verdict. Client mirror of the gateway's
 * `workflow_graph.REVIEW_KINDS`.
 *
 * The two are NOT interchangeable past this predicate, which is why the branches in
 * `canDecide` and `applicableViewerModes` stay per-kind: everything hanging off
 * `organ_mask_review` is mask-specific (the verification checklist, the `seg_state`
 * lock, the two-phase relabelling). What they share is that a disapproval needs a
 * remark and that the buttons say "rework", not "reject".
 */
export function isReviewKind(task: Task): boolean {
  return task.task_kind === 'organ_mask_review' || task.task_kind === 'anomaly_review'
}

/**
 * A row whose lifecycle the configurable workflow engine owns.
 *
 * The distinction matters wherever this file infers something from the *shape* of a study's
 * rows, because the engine's shape is different: `OrganMaskWorkflow` minted ONE review row
 * per rework round at `max_annotators = review_count`, while a graph node's fan-out is N
 * CONCURRENT single-slot rows sharing one `node_id`.
 */
export function isEngineOwned(t: Task): boolean {
  return !!t.owner_workflow_id
}

/**
 * Only the newest *undecided* `organ_mask_review` row per study is live.
 * Skipping `complete` is what makes this correct: without it the newest row was
 * often the one already approved, so the card kept offering Approve & Finalize
 * and the click 500'd on a workflow that had exited.
 *
 * **Engine rows are excluded from the grouping entirely**, and that is a correctness fix,
 * not a shortcut. Keyed on `study_uid`, a two-reviewer graph node's two concurrent rows
 * collapse to one — so the second radiologist's ticket appeared in no list on `/work` and
 * `canDecide` refused it, and with `quorum: all_of_n` the mask could never finalize. There
 * is nothing left for the heuristic to do on those rows either: the engine closes a
 * superseded round itself with `set_task_status(status='complete')`, which the line above
 * already filters.
 */
export function newestUndecidedMaskReviews(tasks: readonly Task[]): Map<string, Task> {
  const newest = new Map<string, Task>()
  for (const t of tasks) {
    if (t.task_kind !== 'organ_mask_review' || t.status === 'complete') continue
    if (isEngineOwned(t)) continue
    const prev = newest.get(t.study_uid)
    if (!prev || t.created_at > prev.created_at) newest.set(t.study_uid, t)
  }
  return newest
}

/**
 * A decided row is not stale — it's the history the reviewer wants to re-read,
 * so it stays visible (read-only, Complete badge) like any other finished task.
 * What this hides is an *undecided* round that a newer one superseded.
 */
export function makeIsLive(tasks: readonly Task[]): (t: Task) => boolean {
  const newest = newestUndecidedMaskReviews(tasks)
  return (t: Task) =>
    t.task_kind !== 'organ_mask_review' ||
    t.status === 'complete' ||
    // Every concurrent slot of a graph node is live at once — see above.
    isEngineOwned(t) ||
    newest.get(t.study_uid)?.id === t.id
}

/**
 * "This row is a duplicate of another slot of the same step, so it is not mine to
 * claim."
 *
 * A graph node with N slots is minted as N single-slot rows sharing one `node_id`
 * (`_mint_rows`), and the whole point of the fan-out is that the slots go to
 * *different* people — two independent reads are not independent if one reader
 * files both. The gateway enforces it in the DB (`ux_tasks_node_assignee`, a partial
 * UNIQUE on `(owner_workflow_id, node_id, assignee_id)`) and 409s the second claim
 * with "it needs a different person".
 *
 * Two things are surplus, and the second is the reported bug:
 *
 * - **A sibling of a slot I already hold.** The row stays `pending`, so without this
 *   the first radiologist's Available tab kept offering a Claim that could only ever
 *   fail.
 * - **Every unclaimed slot but one.** Before anybody claims, all N rows are pending
 *   and identical, so the queue showed N copies of the same ticket — and claiming
 *   one made the others disappear, which is what made it read as a duplicate rather
 *   than as work for someone else. Only the lowest `id` is offered; it is
 *   deterministic and the same for every reader, so all of them see one card and the
 *   next slot is promoted on the refresh after that one is taken.
 *
 * Scoped to **open** rows, matching the index's own `status <> 'complete'`
 * predicate: a rework round and a reset→restart reuse `graph-{uid}` + `node_id`, so
 * a finished slot must not lock the user out of the next round.
 */
export function makeIsSurplusSlot(
  tasks: readonly Task[],
  userId: string,
): (t: Task) => boolean {
  const key = (t: Task) => `${t.owner_workflow_id} ${t.node_id}`
  const held = new Set<string>()
  // Step → the id of the one unclaimed slot it may offer.
  const offer = new Map<string, string>()
  for (const t of tasks) {
    if (!t.owner_workflow_id || !t.node_id || t.status === 'complete') continue
    if (isAssignedToUser(t, userId)) {
      held.add(key(t))
      continue
    }
    // Somebody else's slot. Not a candidate to offer, and not counted — so once a
    // reader takes the visible card, the next one becomes visible.
    if (t.assignee_id) continue
    const k = key(t)
    const cur = offer.get(k)
    if (!cur || t.id < cur) offer.set(k, t.id)
  }
  return (t) => {
    if (!t.owner_workflow_id || !t.node_id || isAssignedToUser(t, userId)) return false
    const k = key(t)
    // `?? t.id` so a row that is nobody's candidate (already claimed by someone
    // else) is left exactly as it was before this rule existed.
    return held.has(k) || (offer.get(k) ?? t.id) !== t.id
  }
}

export interface DecideContext {
  canReview: boolean
  isAssignedToMe: boolean
  /** Maintainer/admin bypass the gateway's ownership check in `review_task`. */
  ownershipExempt: boolean
  isLive: boolean
  /** Whose verdict this would be. A mask review is decidable per reviewer now. */
  currentUserId: string
}

/**
 * Mirrors the gateway's `review_task`: task:review, plus either you hold the row
 * or you're exempt, plus the kind actually has a decision path.
 */
export function canDecide(task: Task, ctx: DecideContext): boolean {
  if (!ctx.canReview) return false
  // An artefact adjudication has its own verdict pair (confirm / send back) on
  // `POST /checklists/artifact/{id}/decision`. `POST /tasks/{id}/review` would
  // signal `task-{id}` — a workflow this kind never had — so the generic card
  // must not claim it, whatever its status becomes.
  if (isArtifactAdjudication(task)) return false
  if (!ctx.isAssignedToMe && !ctx.ownershipExempt) return false
  if (task.task_kind === 'organ_mask_review') {
    // My verdict is spent. The row outlives it — it carries my anomaly read now —
    // so the buttons are still on screen with nothing behind them: the workflow
    // dedups votes by reviewer and the gateway 409s a second approval outright.
    if (myMaskApproval(task, ctx.currentUserId)) return false
    // And the mask must actually be out for a verdict. Both remaining states are
    // newly reachable *with a live review card*, because the ticket survives the
    // rework round it triggers: `editing` means my colleague sent it back and the
    // version I am looking at no longer exists, `finalized`/`quarantine` that
    // `organ-mask-{uid}` has exited and there is nothing to signal. A row with no
    // `seg_state` is pre-redesign, which the gateway treats as decidable.
    // Mirrors `review_task`'s `seg_state not in (None, 'review')` 409.
    if (task.seg_state && task.seg_state !== 'review') return false
    return ctx.isLive && task.status !== 'complete'
  }
  // A read verification is study-owned, so the fallback below would refuse it and
  // grey out both verdict buttons with nothing to explain why. None of the mask
  // guards above apply: there is no mask under it, so no `seg_state` to check and
  // no second reviewer to dedup against — the engine's own quorum does that.
  if (task.task_kind === 'anomaly_review') return task.status !== 'complete'
  return (
    !STUDY_OWNED.has(task.task_kind) &&
    (task.status === 'under_review' || (task.status === 'submitted' && task.requires_review))
  )
}

/**
 * Dual reads in flight. No decision path by design — agreement auto-finalizes
 * in the workflow and a disagreement routes to the flagged groups below. Listed
 * so a reviewer can still see what's waiting on a second reader.
 */
export function isInFlightDual(task: Task): boolean {
  return (
    // `anomalyPhaseOpen`, not the per-reader `isAnomalyRead`: this is a reviewer's
    // "what is waiting on a second reader" list, and a maintainer reading it holds
    // no approval of their own on any of these rows.
    (anomalyPhaseOpen(task) ||
      task.task_kind === 'anomaly_annotation' ||
      task.task_kind === 'pathology_annotation') &&
    task.status !== 'complete' &&
    !task.requires_maintainer
  )
}

// ---------------------------------------------------------------------------
// Flagged work — lifted from FlaggedTab.tsx.
// ---------------------------------------------------------------------------

/**
 * Flagged *annotation* work. `qc_review` also carries `requires_maintainer`, but
 * it's an ingestion QC flag with no per-task workflow, so it would land in
 * `MaskFlagCard`, whose buttons signal `organ-mask-{study_uid}`. It's resolved by
 * `QcReviewCard` (`POST /ingest/review/{wfid}`), which is keyed on the workflow
 * id and reads `study_status`, so the task row must not be rendered twice.
 */
export function isFlaggedWork(t: Task): boolean {
  // `requires_maintainer` is never cleared once the workflow sets it, so a
  // resolved escalation still reads as flagged. `status` is the real signal —
  // without this check the card outlived its workflow and its buttons 500'd.
  return !!t.requires_maintainer && t.task_kind !== 'qc_review' && t.status !== 'complete'
}

/**
 * A flagged row whose flag came from the **reader** track, not the mask's.
 *
 * The kind cannot answer this on its own: the anomaly track adopts the
 * `organ_mask_review` row (`reuse_upstream_ticket`), so a reader disagreement is
 * filed on a row whose `task_kind` still says mask review and whose `tasks.title`
 * still reads "Verify the organ mask". `anomaly_state` is the fact that separates
 * them.
 */
export function isReaderDisagreementRow(t: Task): boolean {
  return (
    t.task_kind === 'anomaly_annotation' ||
    t.task_kind === 'pathology_annotation' ||
    t.anomaly_state === 'disagreement'
  )
}

export interface FlaggedGroup {
  studyUid: string
  /** Which card renders it: a reader tiebreak, or an escalated organ mask. */
  kind: 'tiebreak' | 'mask_escalation'
  /** Every flagged row for that study — a 2-slot node is two rows, one card. */
  tasks: Task[]
}

/**
 * Flagged task rows, grouped into **one entry per card that will be rendered**.
 *
 * The grouping itself is old (`FlaggedDecisions` has always rendered one card per
 * study and fanned a node's slots back in); what is new is that it lives here, so
 * the counters can no longer disagree with the cards. They did: `/work`'s
 * Needs-decision badge and the maintainer home's tile both counted *rows*, and a
 * 2-slot read node is two rows — so one tiebreak card was announced as two
 * decisions, which reads as two tickets for work that is one. The home page's
 * tile mislabelled them on top of that, filing a reader disagreement under
 * "Escalated masks" with a hint about the rework limit.
 *
 * Rows that are not flagged are dropped, so callers may pass an unfiltered list.
 */
export function groupFlaggedWork(tasks: readonly Task[]): FlaggedGroup[] {
  const byStudy = new Map<string, Task[]>()
  for (const t of tasks) {
    if (!isFlaggedWork(t)) continue
    const arr = byStudy.get(t.study_uid) ?? []
    arr.push(t)
    byStudy.set(t.study_uid, arr)
  }
  const groups: FlaggedGroup[] = []
  for (const [studyUid, rows] of byStudy) {
    // ALL the reader rows, not the first: the card fans a node's slots back in so
    // the maintainer has both reads to pick from. If any row is a reader row the
    // whole group is a tiebreak — its buttons signal the graph node, whereas
    // `MaskFlagCard`'s signal `organ-mask-{uid}`, a workflow that has exited.
    const readerRows = rows.filter(isReaderDisagreementRow)
    groups.push(
      readerRows.length > 0
        ? { studyUid, kind: 'tiebreak', tasks: readerRows }
        : { studyUid, kind: 'mask_escalation', tasks: rows },
    )
  }
  return groups
}

/** Studies the server's `flagged` filter returns. Keep in step with
 *  `_FLAGGED_SQL` in the gateway's studies.py:
 *  `seg_state IN ('auto_seg_failed','awaiting_maintainer') OR anomaly_state = 'disagreement'`.
 *  A study that matches neither arm is not flagged, so it gets no card at all —
 *  the previous code fell through to an anomaly card for anything non-seg. */
export const STUDY_FLAG_SEG_STATES = ['auto_seg_failed', 'awaiting_maintainer'] as const

export function flaggedStudyKind(
  s: StudyRow,
): 'seg_failure' | 'anomaly_disagreement' | null {
  if (STUDY_FLAG_SEG_STATES.includes(s.seg_state as (typeof STUDY_FLAG_SEG_STATES)[number])) {
    return 'seg_failure'
  }
  if (s.anomaly_state === 'disagreement') return 'anomaly_disagreement'
  return null
}

// ---------------------------------------------------------------------------
// The action ladder. Six rungs, first match wins — the order is load-bearing:
// a mask review is `assigned` and a submission awaiting a verdict is terminal,
// so a lower rung would otherwise swallow the review card.
// ---------------------------------------------------------------------------

export type TaskAction =
  /** Reviewer verdict UI (approve / reject / rework). */
  | 'decide'
  /** Navigate elsewhere to work the task (maintainer "My work" affordance). */
  | 'open'
  /** Viewer links + Submit. */
  | 'submit'
  /** Viewer links + Mark annotated. */
  | 'mark_annotated'
  /** Viewer buttons only. */
  | 'viewers'
  | 'claim'
  /** Claimed by the max number of annotators, none of them me. */
  | 'full'
  /** Post-submission: read-only re-view + status label. */
  | 'terminal'
  /** Explicit read-only render (the Activity list). */
  | 'read_only'
  | 'none'

export interface ActionInputs {
  task: Task
  currentUserId: string
  phase: AnnotatePhase
  /** `task:tag`, after the "no gating props supplied" escape hatch. */
  canTag: boolean
  roleAllowsClaim: boolean
  readOnly: boolean
  hasDecide: boolean
  hasOpen: boolean
  hasOpenViewer: boolean
  hasSubmit: boolean
  hasMarkAnnotated: boolean
  hasClaim: boolean
}

export function taskAction(i: ActionInputs): TaskAction {
  const { task, currentUserId, phase } = i
  if (i.readOnly) return 'read_only'
  const assigned = isAssignedToUser(task, currentUserId)
  const terminal = isTerminal(task)

  // Deciding comes first in the ladder: a mask review is `assigned` and a
  // submission awaiting a verdict is in TERMINAL_STATUSES, so either of the
  // rungs below would otherwise swallow the review card.
  if (i.hasDecide) return 'decide'
  // My own submission is already filed. A dual-reader row stays `assigned` until
  // both reads are in, so without this the card dropped back to the viewer rung
  // and offered Submit a second time — which the gateway 409s, because a clinical
  // read is final at submit.
  if (assigned && !terminal && mySubmissionIn(task, currentUserId)) return 'terminal'
  if (assigned && i.hasOpen && !terminal && !i.hasOpenViewer) return 'open'
  if (assigned && i.hasOpenViewer && !terminal) {
    if (phase === 'pending_submit' && i.hasSubmit && i.canTag) return 'submit'
    if (phase === 'annotating' && i.hasMarkAnnotated && i.canTag) return 'mark_annotated'
    return 'viewers'
  }
  if (i.hasClaim && isClaimable(task, currentUserId) && i.canTag && i.roleAllowsClaim) {
    return 'claim'
  }
  if (isFullyClaimed(task) && !assigned) return 'full'
  // Post-submission, the assignee can re-open the study in basic OHIF
  // (read-only) — the gateway's _resolve_study_access returns "read_only"
  // for technicians on studies where every task they hold is post-submit,
  // so DICOMweb GETs succeed but writes/MONAI are blocked. Only Basic mode
  // here: the combined Annotate mode is an authoring tool and isn't
  // appropriate after the task is complete.
  if (terminal) return 'terminal'
  return 'none'
}

// ---------------------------------------------------------------------------
// What the card says. Derived, never stored, because
// `tasks.title` is written ONCE at task creation (`_title_for_kind` in
// tagging-worker's activities/organ_mask.py) and never rewritten: on a merged
// radiologist ticket it still reads "Review organ mask: {uid}" while the row is
// carrying the anomaly read. Patching the row would need a new activity command
// in `AnomalyWorkflow` (which has a replay fixture) and still wouldn't fix rows
// already adopted. The heading is derived data, so derive it — here, once, where
// the card, the home rows and the notification bell all read it.
// ---------------------------------------------------------------------------

/**
 * `anomaly_state` values that mean phase two has been reached on an adopted
 * `organ_mask_review` row.
 *
 * `quarantine` is deliberately NOT one of them: `anomaly_state` is a *study*
 * column joined onto the task, and a study whose seg track quarantined reaches
 * `anomaly_state='quarantine'` without `AnomalyWorkflow` ever adopting the review
 * row (it returns before adoption). That ticket is still a mask verification.
 */
const ANOMALY_PHASE_REACHED: ReadonlySet<string> = new Set([
  'annotating',
  'comparing',
  'disagreement',
  'finalized',
  // `no_abnormality` IS one of them, unlike `quarantine`: it is only reachable
  // from a radiologist's checklist, whose variant only exists once the row has
  // been adopted for the read. So adoption did happen, and the ticket is an
  // anomaly read that came back negative.
  'no_abnormality',
])

/** The work, as an imperative. No study UID — the card shows that on its own. */
const TICKET_TITLE: Record<TaskKind, string> = {
  organ_mask_segmentation: 'Segment the organs',
  ground_truth_annotation: 'Annotate ground truth',
  qa_check: 'Check a submitted annotation',
  organ_mask_edit: 'Edit the organ mask',
  organ_mask_review: 'Verify the organ mask',
  vessel_mask_edit: 'Segment coronary vessels',
  anomaly_annotation: 'Read for anomalies',
  anomaly_review: 'Verify the anomaly read',
  pathology_annotation: 'Read for pathology',
  qc_review: 'Resolve the ingestion QC hold',
  artifact_review: 'Confirm an artefact reject',
  // A generic fallback only. Every `form_capture` row comes from a graph node
  // that carries its own `label`, and `ticketTitle` prefers that — this is what
  // shows if a row somehow predates its node config.
  form_capture: 'Complete the form',
}

/**
 * Has THIS reader's ticket become the anomaly read?
 *
 * The card's phase switch: one claim covers verifying the technician's mask and then
 * reading for anomalies on it, and everything mask-flavoured on the card (organ
 * chips, the retry count, the promote/index wait) belongs to the first half only.
 * Showing both halves at once was the reported clutter.
 *
 * Two halves, both required. `ANOMALY_PHASE_REACHED` is the *study* having opened
 * its reads; `readerPhaseTwoOpen` is this reader's own approval — the reads open per
 * radiologist, so on the first approval one reader's card turns over while the
 * other's is still a mask verification.
 *
 * Deliberately NOT `isAnomalyRead`, which is `annotating`-only because it drives
 * `applicableViewerModes` (a `finalized` read must not still offer the anomaly
 * editor). A card that has *become* the read stays one after it is filed.
 */
export function inAnomalyPhase(task: Task, userId: string): boolean {
  if (task.task_kind === 'anomaly_annotation') return true
  if (task.task_kind !== 'organ_mask_review') return false
  return (
    ANOMALY_PHASE_REACHED.has(task.anomaly_state ?? '') && readerPhaseTwoOpen(task, userId)
  )
}

/**
 * The heading for a ticket — never `task.title`, which goes stale on adoption.
 *
 * `userId` is optional only for the observer surfaces (the home row lists), where
 * every row is pre-claim or pre-read and the work title is the right one. Anywhere
 * a radiologist acts on the ticket, pass it: without it the heading is study-level,
 * so the first approval renamed BOTH readers' cards "Read for anomalies" while one
 * of them was still verifying the mask.
 */
export function ticketTitle(task: Task, userId = ''): string {
  if (task.task_kind === 'organ_mask_review' && inAnomalyPhase(task, userId)) {
    return 'Read for anomalies'
  }
  return TICKET_TITLE[task.task_kind] ?? task.title
}

/**
 * An anomaly read follows this mask verdict — i.e. the study is anomalous and the
 * read is parked on (or already open on) this same ticket. False on a healthy
 * study, where a mask verdict is the whole ticket and nothing opens after it.
 */
export function anomalyReadFollows(task: Task): boolean {
  if (task.task_kind !== 'organ_mask_review') return false
  const state = task.anomaly_state ?? ''
  return state === 'waiting_for_mask' || ANOMALY_PHASE_REACHED.has(state)
}

/**
 * I am reading on a mask version that has since been sent back.
 *
 * The honest consequence of reading independently: I approved v1 and started my
 * read, my colleague rejected it, and the technician is now changing the paint
 * under me. My filed read still stands — a clinical opinion is not retracted by
 * someone else's verdict — and the *final* mask is the last approved version, so
 * this says so rather than letting the reader discover it from a volumetric
 * disagreement they cannot explain.
 *
 * `rejection_count > 0` is what distinguishes it from the ordinary case of simply
 * being first to approve, where nothing has changed yet.
 */
export function readingOnASupersededMask(task: Task, userId: string): boolean {
  return (
    myMaskApproval(task, userId) &&
    task.seg_state !== 'finalized' &&
    (task.rejection_count ?? 0) > 0
  )
}

/**
 * The mask I still have to verify is back with the technician right now.
 *
 * The review row survives every rework round (it carries both radiologists'
 * anomaly phase), so this reviewer keeps a card whose verdict buttons are dead
 * until the technician resubmits — `seg_state` flips `editing` → `review` and
 * `canDecide` opens again. Without saying so, the card reads "check the
 * technician's mask slice by slice" over a mask that is being repainted, and the
 * reviewer has no way to tell whether they are waiting on a queue they should be
 * watching.
 *
 * Scoped to a reviewer with no approval of their own: whoever approved is reading,
 * not waiting, and `readingOnASupersededMask` is their sentence.
 */
export function maskOutForRework(task: Task, userId: string): boolean {
  return (
    task.task_kind === 'organ_mask_review' &&
    task.seg_state === 'editing' &&
    !myMaskApproval(task, userId)
  )
}

/**
 * The round now in flight was opened by MY rejection.
 *
 * Either radiologist can send a mask back, in any order, so `maskOutForRework` is
 * true for both of them at once — for the one who asked for the rework and for the
 * colleague who was still checking the version it replaced. Telling the second one
 * "Sent back for rework" credits them with a verdict they never cast, on the one
 * card where the difference decides whether they have anything left to do.
 *
 * The iteration arithmetic: a rejection is cast at `rejection_count + 1` and the
 * workflow then bumps the counter, so the vote that opened this round is the one
 * filed at `rejection_count` exactly. Two radiologists rejecting the same version
 * both file there and both get the first person plural — they both objected, and
 * only one round was opened for the two of them.
 */
export function iOpenedThisReworkRound(task: Task, userId: string): boolean {
  if (!userId) return false
  const round = task.rejection_count || 0
  if (round < 1) return false
  return (task.review_remarks ?? []).some(
    (r) =>
      r.reviewer_id === userId &&
      Number(r.iteration ?? 0) === round &&
      String(r.decision ?? '') === 'reject',
  )
}

export interface NextStepInputs {
  task: Task
  action: TaskAction
  /** Whose next step this is. A merged ticket's phase is per radiologist. */
  currentUserId: string
  /** `maskLockReason(task, reworkLimit)` — the blocking fact when there is one. */
  lockReason: string | null
  isProcessing: boolean
  /** My verdict landed a moment ago and the row hasn't caught up. */
  hasVoted?: boolean
  /** Which verdict that was. An approve and a reject now diverge *before* the row
   *  catches up: mine opens my read, my colleague's sends the mask back. */
  votedDecision?: 'approved' | 'reject' | null
  review: ReviewProgress
  /** `mySubmissionIn(task, userId)`. */
  myReadIn: boolean
}

/**
 * The one sentence that says what to do next. First match wins.
 *
 * One directive replaces the four grey paragraphs this card used to stack (lock
 * reason, phase line, processing line, outstanding-reviewers line), each of which
 * explained a different fragment of state and none of which named an action.
 */
export function nextStep(i: NextStepInputs): string | null {
  const { task, action, review } = i
  // A history row (`/activity`) is not asking anyone to do anything.
  if (action === 'read_only' || action === 'none') return null

  // Echoes of my mask verdict. Once my read is open on this same row none of them is
  // my next step — the read directives below are. `WorkPage`'s `voted` map is local
  // and never pruned, so without this guard the promote/index line sat on a card
  // whose headline had already turned over to the anomaly read.
  if (!inAnomalyPhase(task, i.currentUserId)) {
    if (i.isProcessing) {
      return 'Approved. The mask is being promoted and indexed — a few minutes, and it clears on its own.'
    }
    if (i.hasVoted) {
      if (i.votedDecision === 'approved') {
        // Mine was not the last approval, so nothing is being promoted — but my own
        // read opens on it regardless. That independence is the whole change, and
        // this is the moment the radiologist needs told about it.
        return anomalyReadFollows(task)
          ? 'Approved. Your anomaly read opens on this same ticket in a moment — you do not have to wait for the other radiologist.'
          : 'Approval recorded. The mask moves once the other radiologist has approved it too.'
      }
      // The rework path: instant, and the card is about to turn over on the next
      // refresh.
      return 'Rework requested. The technician picks it up from here.'
    }
  }
  // My read is open. Whatever my colleague is still doing to the mask, waiting is
  // not my next step — so the read directives below win.
  if (review.waiting && !anomalyPhase(task, i.currentUserId)) {
    const what = review.myDecision === 'reject' ? 'rework' : 'approval'
    const who = review.outstanding === 1 ? 'reviewer' : 'reviewers'
    return `Your ${what} verdict is recorded. Waiting for ${review.outstanding} more ${who} before this mask moves.`
  }
  if (action === 'decide') {
    return task.task_kind === 'organ_mask_review'
      ? 'Compare the mask against the CT, then approve it or send it back with a remark.'
      : 'Check the submitted annotation, then approve or reject it.'
  }
  // Held, but there is nothing to verify: the mask is being redrawn. Said before
  // the working rungs below, which would otherwise tell this reviewer to go and
  // check a mask that is mid-edit.
  if (isAssignedToUser(task, i.currentUserId) && maskOutForRework(task, i.currentUserId)) {
    return iOpenedThisReworkRound(task, i.currentUserId)
      ? 'Sent back for rework. The technician is redrawing it — the verdict buttons come back on this card as soon as they resubmit, so there is nothing to watch for.'
      : 'The other radiologist sent this mask back, so the version you were checking is being redrawn. Your verdict applies to the new one — the buttons come back on this card as soon as the technician resubmits.'
  }
  if (action === 'terminal') {
    if (i.myReadIn && !isTerminal(task)) {
      return 'Your read is filed. Nothing further until the second radiologist files theirs.'
    }
    return task.status === 'complete'
      ? 'Complete — nothing left to do.'
      : 'Submitted — a reviewer takes it from here.'
  }
  if (action === 'full') return 'Both slots are taken. Nothing to claim here.'
  if (action === 'open') return 'Open it on the work surface to get started.'

  // Working rungs from here down. A locked mask beats any instruction to edit it.
  if (i.lockReason) return `${i.lockReason}.`

  if (action === 'submit') {
    if (task.task_kind === 'organ_mask_edit') {
      return 'Submit to send the mask to the radiologists for review.'
    }
    return 'Submit your work.'
  }
  if (action === 'mark_annotated') {
    return 'Finished in the viewer? Mark it annotated, then submit.'
  }
  if (action === 'viewers') {
    if (isAnomalyRead(task, i.currentUserId)) {
      // Say it here, on the rung where they are about to go and draw: the mask
      // moved after they approved it, and their read is on the version they saw.
      return readingOnASupersededMask(task, i.currentUserId)
        ? 'Open the anomaly viewer, paint every anomalous region with Marker Guided AI, and submit your read from the panel there — there is nothing further to do on this card. The other radiologist sent the mask back for rework — your read stands on the version you approved.'
        : 'Open the anomaly viewer, paint every anomalous region with Marker Guided AI, and submit your read from the panel there — there is nothing further to do on this card.'
    }
    if (task.task_kind === 'organ_mask_review') {
      return "Open the viewer and check the technician's mask slice by slice."
    }
    if (task.task_kind === 'qa_check') {
      return 'Open the viewer and check the annotation against the brief.'
    }
    if (task.task_kind === 'ground_truth_annotation') {
      return 'Open the editor and annotate the study.'
    }
    return 'Open the mask editor and paint the organs.'
  }
  // claim
  if (task.task_kind === 'organ_mask_review' && task.anomaly_state === 'waiting_for_mask') {
    return "Claim it to verify the technician's mask. Your anomaly read opens on this same ticket as soon as you approve it — no waiting on the other radiologist."
  }
  const work = ticketTitle(task, i.currentUserId)
  return `Claim it to ${work.charAt(0).toLowerCase()}${work.slice(1)}.`
}

// ---------------------------------------------------------------------------
// Key action items — the card's stepper. A position on a fixed ladder per kind,
// derived from the same `TaskAction` + local annotate phase the buttons use, so
// the dots can never disagree with the button sitting under them.
// ---------------------------------------------------------------------------

export interface ActionSteps {
  /** Step names. Not rendered as text — they label the dots for screen readers. */
  steps: readonly string[]
  /** Index of the step the user is on; `steps.length` = every step done. */
  current: number
}

const EDIT_STEPS = ['Claim', 'Edit', 'Mark annotated', 'Submit'] as const
const VERIFY_STEPS = ['Claim', 'Verify mask', 'Verdict'] as const
const VERIFY_AND_READ_STEPS = [...VERIFY_STEPS, 'Read anomalies'] as const
const READ_STEPS = ['Claim', 'Paint anomalies', 'Submit in viewer'] as const

/**
 * Where this card sits on its ladder, or `null` when there is no ladder to show:
 * a history row, a slot that is full, a verdict on a kind that is not a claimed
 * ticket (the generic approve/reject path), or anything with no next action.
 *
 * `phase` is the local annotate phase: `annotating` means this tab opened the
 * viewer for the task, which is what moves "Verify mask" on to "Verdict" and
 * "Paint anomalies" on to "Submit in viewer". Like the technician's Mark
 * annotated, it is local and resets on reload — the card falls one dot back,
 * nothing breaks.
 */
export function actionSteps(i: NextStepInputs & { phase: AnnotatePhase }): ActionSteps | null {
  const { task, action, phase, currentUserId: me } = i
  if (action === 'read_only' || action === 'none' || action === 'full' || action === 'open') {
    return null
  }
  const viewed = phase !== 'idle'

  if (task.task_kind === 'organ_mask_review') {
    const steps = anomalyReadFollows(task) ? VERIFY_AND_READ_STEPS : VERIFY_STEPS
    const done = steps.length
    if (action === 'claim') return { steps, current: 0 }
    if (inAnomalyPhase(task, me)) return { steps, current: i.myReadIn ? done : 3 }
    const approved =
      myMaskApproval(task, me) ||
      i.review.myDecision === 'approved' ||
      (i.hasVoted && i.votedDecision === 'approved')
    // Approved, read not open yet: the next dot is the read (or, healthy, nothing).
    if (approved) return { steps, current: Math.min(3, done) }
    if (action === 'decide') return { steps, current: viewed ? 2 : 1 }
    if (action === 'terminal') return { steps, current: done }
    // Sent back (by me or my colleague) or otherwise waiting: verifying again is
    // the next real step, once the technician resubmits.
    return { steps, current: 1 }
  }

  // Any other verdict card is the generic approve/reject on a submission nobody
  // claimed through this ladder.
  if (action === 'decide') return null

  if (task.task_kind === 'anomaly_annotation') {
    if (action === 'claim') return { steps: READ_STEPS, current: 0 }
    if (action === 'terminal' || i.myReadIn) return { steps: READ_STEPS, current: 3 }
    return { steps: READ_STEPS, current: viewed ? 2 : 1 }
  }

  const current = { claim: 0, viewers: 1, mark_annotated: 2, submit: 3, terminal: 4 }[
    action as 'claim' | 'viewers' | 'mark_annotated' | 'submit' | 'terminal'
  ]
  return current === undefined ? null : { steps: EDIT_STEPS, current }
}

/**
 * The fixed instruction for this kind of work — the card's info banner. Unlike
 * `nextStep`, it does not move with state: it says what "done" means for the
 * whole ticket. `null` for kinds whose brief is the job description alone.
 */
export function ticketBrief(task: Task, userId: string): string | null {
  if (inAnomalyPhase(task, userId)) {
    return 'Paint every anomalous region. The mask is context, not the subject.'
  }
  if (task.task_kind === 'organ_mask_review') {
    return 'Every listed structure must be present and correctly labelled before you approve.'
  }
  if (task.task_kind === 'organ_mask_edit') {
    return 'Correct the auto generated mask. Every listed structure must be present and correctly labelled.'
  }
  if (task.task_kind === 'vessel_mask_edit') {
    return 'Correct the Lumen, compute the centerline, label the paths, then draw the Wall.'
  }
  return null
}

/**
 * Permission gating for the card. When neither role nor permissions are supplied
 * (legacy layout-only test callers), gating is a no-op so those tests still
 * work. When supplied, actions a user cannot perform are hidden —
 * defense-in-depth for the page-level guards.
 */
export function cardGating(role?: Role, permissions?: string[]) {
  const gatingProvided = role !== undefined || permissions !== undefined
  return {
    canTag: !gatingProvided || (permissions?.includes('task:tag') ?? false),
    // Maintainers bulk-assign + review; they don't tag themselves except via
    // explicit assignment. Don't expose Claim to maintainer/developer roles.
    roleAllowsClaim:
      !gatingProvided ||
      role === 'admin' ||
      role === 'technician' ||
      role === 'qa' ||
      // Radiologists self-claim their own work from this queue: organ_mask_review
      // verdicts and anomaly reads. Omitting them here left the Claim button
      // hidden even though the API allowed the claim.
      role === 'radiologist' ||
      // maintainers can technically self-claim if api allows, but UI hides it
      role === undefined,
  }
}

/**
 * `/work`'s study-ID filter: a trimmed, case-insensitive substring match, so a
 * pasted tail of a UID finds its study. An empty query matches everything.
 * Filtering by study keeps every row of a study together, which the per-step
 * predicates (`makeIsLive`, `makeIsSurplusSlot`) rely on.
 */
export function matchesStudy(studyUid: string, query: string): boolean {
  const q = query.trim().toLowerCase()
  return !q || studyUid.toLowerCase().includes(q)
}
